import { useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import {
  FiArrowRight,
  FiCheck,
  FiAlertCircle,
  FiHome,
  FiDollarSign,
  FiPercent,
  FiMapPin,
} from "@/lib/icons";
import { cn } from "@/lib/utils";

type StateCode = "NSW" | "VIC" | "QLD" | "WA" | "SA" | "TAS" | "ACT" | "NT";

interface StateInfo {
  name: string;
  code: StateCode;
  fhog: {
    amount: string;
    conditions: string[];
    propertyValueCap: string;
    newHomesOnly: boolean;
  };
  stampDuty: {
    exemptionThreshold: string;
    concessionThreshold: string;
    notes: string[];
  };
  additionalSchemes: {
    name: string;
    description: string;
  }[];
  highlight?: string;
}

const stateData: Record<StateCode, StateInfo> = {
  NSW: {
    name: "New South Wales",
    code: "NSW",
    fhog: {
      amount: "$10,000",
      conditions: [
        "Must be a new home (newly built, off-the-plan, or substantially renovated)",
        "You must be 18 years or older",
        "You or your spouse must not have previously owned property in Australia",
        "You must move into the home within 12 months and live there for at least 6 months",
      ],
      propertyValueCap: "$600,000 (or $750,000 for new homes in Sydney)",
      newHomesOnly: true,
    },
    stampDuty: {
      exemptionThreshold: "$800,000 (homes) / $350,000 (land)",
      concessionThreshold: "$1,000,000 (homes) / $450,000 (land)",
      notes: [
        "Full exemption for new and existing homes up to $800,000",
        "Sliding scale concession between $800,000 - $1,000,000",
        "Applies to both new and existing homes",
      ],
    },
    additionalSchemes: [
      {
        name: "First Home Buyer Assistance Scheme",
        description:
          "Stamp duty exemptions and concessions for first home buyers on both new and existing properties",
      },
      {
        name: "Shared Equity Home Buyer Helper",
        description:
          "Government contributes up to 40% of purchase price for new homes, 30% for existing",
      },
    ],
  },
  VIC: {
    name: "Victoria",
    code: "VIC",
    fhog: {
      amount: "$10,000",
      conditions: [
        "Must be a new home valued at $750,000 or less",
        "You must be 18 years or older",
        "Must be an Australian citizen or permanent resident",
        "You must live in the home as your principal place of residence for at least 12 months",
      ],
      propertyValueCap: "$750,000",
      newHomesOnly: true,
    },
    stampDuty: {
      exemptionThreshold: "$600,000",
      concessionThreshold: "$750,000",
      notes: [
        "Full exemption for properties under $600,000",
        "Concession available for properties $600,000 - $750,000",
        "Applies to all homes (new and existing)",
      ],
    },
    additionalSchemes: [
      {
        name: "Victorian Homebuyer Fund",
        description:
          "Shared equity scheme where government contributes up to 25% of purchase price. Buy with just 5% deposit",
      },
    ],
  },
  QLD: {
    name: "Queensland",
    code: "QLD",
    highlight: "Best value for first home buyers in 2026",
    fhog: {
      amount: "$30,000",
      conditions: [
        "Must be a new home (never previously occupied)",
        "Contract signed between 20 Nov 2023 - 30 June 2026",
        "Property must be valued under $750,000",
        "Must live in the home for at least 1 year within 1 year of settlement",
      ],
      propertyValueCap: "$750,000",
      newHomesOnly: true,
    },
    stampDuty: {
      exemptionThreshold: "No cap for new homes (since May 2025)",
      concessionThreshold: "$800,000 for existing homes",
      notes: [
        "ZERO stamp duty on NEW homes regardless of price (since May 2025)",
        "First Home Concession for existing homes up to $800,000",
        "Potential savings of $55,000+ when combining grants and concessions",
      ],
    },
    additionalSchemes: [
      {
        name: "Regional Home Building Boost",
        description:
          "Additional $5,000 grant for building a new home in regional Queensland",
      },
    ],
  },
  WA: {
    name: "Western Australia",
    code: "WA",
    fhog: {
      amount: "$10,000",
      conditions: [
        "Must be a new home (newly built or substantially renovated)",
        "Property value cap of $750,000",
        "Must be your principal place of residence",
        "Must live in the home for at least 6 months continuously",
      ],
      propertyValueCap: "$750,000",
      newHomesOnly: true,
    },
    stampDuty: {
      exemptionThreshold: "$500,000",
      concessionThreshold: "$750,000",
      notes: [
        "No stamp duty on new home purchases up to $500,000",
        "Concessional rates for properties $500,000 - $750,000",
        "FHOG applies for new homes up to $750,000",
      ],
    },
    additionalSchemes: [
      {
        name: "Keystart Home Loans",
        description:
          "Low deposit home loans (2% deposit) for eligible first home buyers with no LMI",
      },
    ],
  },
  SA: {
    name: "South Australia",
    code: "SA",
    fhog: {
      amount: "$15,000",
      conditions: [
        "Must be a new home",
        "No property value cap (removed June 2024)",
        "Must be an Australian citizen or permanent resident",
        "Must live in the home for at least 6 months within 12 months",
      ],
      propertyValueCap: "No cap",
      newHomesOnly: true,
    },
    stampDuty: {
      exemptionThreshold: "No cap for new homes",
      concessionThreshold: "N/A",
      notes: [
        "ZERO stamp duty for all eligible first home buyers purchasing new homes (no price cap)",
        "Includes new houses, off-the-plan apartments, house and land packages, and vacant land",
        "Property value limits have been removed",
      ],
    },
    additionalSchemes: [],
  },
  TAS: {
    name: "Tasmania",
    code: "TAS",
    highlight: "$30,000 grant for new builds from January 2026",
    fhog: {
      amount: "$30,000",
      conditions: [
        "Must be building a new home (tripled from $10,000 since 1 January 2026)",
        "No property value cap",
        "Must be your principal place of residence",
        "Must live in the home for at least 6 months",
      ],
      propertyValueCap: "No cap",
      newHomesOnly: true,
    },
    stampDuty: {
      exemptionThreshold: "$750,000 (established homes)",
      concessionThreshold: "N/A",
      notes: [
        "100% stamp duty exemption on established homes up to $750,000",
        "50% discount on established homes up to $600,000 for other buyers",
      ],
    },
    additionalSchemes: [
      {
        name: "MyHome Shared Equity",
        description:
          "Government contributes up to 30% of purchase price. Minimum 2% deposit required",
      },
    ],
  },
  ACT: {
    name: "Australian Capital Territory",
    code: "ACT",
    fhog: {
      amount: "No FHOG",
      conditions: [
        "ACT does not offer a First Home Owner Grant",
        "Replaced by Home Buyer Concession Scheme",
        "Provides stamp duty concessions instead",
      ],
      propertyValueCap: "N/A",
      newHomesOnly: false,
    },
    stampDuty: {
      exemptionThreshold: "Up to $1,020,000 (income tested)",
      concessionThreshold: "$1,020,000 - $1,455,000",
      notes: [
        "Home Buyer Concession Scheme provides stamp duty exemptions",
        "No duty on properties up to $1,020,000 (from 1 July 2025)",
        "Maximum concession of $35,238 for 2025-26",
        "Amount depends on income, property value, and dependents",
      ],
    },
    additionalSchemes: [
      {
        name: "Home Buyer Concession Scheme",
        description:
          "Income-tested stamp duty concessions for properties up to $1,020,000",
      },
    ],
  },
  NT: {
    name: "Northern Territory",
    code: "NT",
    highlight: "Highest grant for new builds ($50,000)",
    fhog: {
      amount: "$50,000 (new) / $10,000 (existing)",
      conditions: [
        "HomeGrown Territory Grant program",
        "Contract must be signed by 30 September 2027",
        "$10,000 for established homes ceased 30 September 2025",
        "No income or property value caps",
        "Must live in the home for at least 12 months",
      ],
      propertyValueCap: "No cap",
      newHomesOnly: false,
    },
    stampDuty: {
      exemptionThreshold: "No first home buyer exemption",
      concessionThreshold: "N/A",
      notes: [
        "No specific first home buyer stamp duty concessions",
        "House and Land Package Exemption available until June 2027",
        "Standard stamp duty rates apply",
      ],
    },
    additionalSchemes: [
      {
        name: "HomeBuild Access",
        description:
          "Up to $20,000 deposit assistance for eligible Territorians building a new home",
      },
    ],
  },
};

const federalSchemes = [
  {
    name: "First Home Guarantee",
    description:
      "Buy with just a 5% deposit and no Lenders Mortgage Insurance. From 1 October 2025, there are no income caps and places are uncapped.",
    eligibility: [
      "Minimum 5% deposit of the property value",
      "Australian citizen or permanent resident",
      "18 years or older",
      "First home buyer (or haven't owned in 10 years)",
      "Will live in the property as your home",
      "No income caps and unlimited places from 1 October 2025",
    ],
  },
  {
    name: "Help to Buy",
    description:
      "Government contributes up to 40% (new homes) or 30% (existing) of purchase price. Buy with as little as 2% deposit.",
    eligibility: [
      "Annual taxable income at or below $100,000 (individual) or $160,000 (single parents and joint applicants) per ATO Notice of Assessment",
      "Property price caps vary by location",
      "Must not currently own property",
      "Must occupy as principal residence",
    ],
  },
  {
    name: "Family Home Guarantee",
    description:
      "For single parents or legal guardians with dependents. Buy with just 2% deposit and no LMI. No income caps since October 2025.",
    eligibility: [
      "Single parent or legal guardian with at least one dependent",
      "Australian citizen or permanent resident",
      "No income caps and unlimited places since 1 October 2025",
      "First home buyer or haven't owned in 10 years",
    ],
  },
];

export function FirstHomeBuyerGuidePage() {
  const [selectedState, setSelectedState] = useState<StateCode>("NSW");
  const state = stateData[selectedState];

  return (
    <>
      <SEO
        title="First Home Buyer Guide - Grants & Schemes by State"
        description="Complete guide to first home buyer grants, stamp duty concessions, and government schemes across all Australian states and territories. Updated for 2026."
        url="/guides/first-home-buyer-guide"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Link
            to="/guides"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
          >
            <FiArrowRight className="size-4 mr-2 rotate-180" />
            Back to Guides
          </Link>
          <p className="section-label mb-4">Guide</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-6">
            First Home Buyer Guide
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Buying your first home is one of life's biggest financial decisions.
            The good news? There are significant grants and concessions available
            to help you get there sooner.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-muted-foreground">
            <span>10 min read</span>
            <span>•</span>
            <span>Updated March 2026</span>
          </div>
        </div>
      </section>

      {/* Federal Schemes */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-12">
            <p className="section-label mb-4">Available Nationwide</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Federal Government Schemes
            </h2>
            <p className="text-muted-foreground">
              These schemes are available to eligible first home buyers across
              Australia, and can be used in conjunction with state grants.
            </p>
          </div>

          <div className="space-y-6">
            {federalSchemes.map((scheme) => (
              <div
                key={scheme.name}
                className="bg-white border border-border/50 rounded-2xl p-6 md:p-8"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
                    <FiHome className="size-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-xl mb-2">{scheme.name}</h3>
                    <p className="text-muted-foreground mb-4">
                      {scheme.description}
                    </p>
                    <div className="bg-muted rounded-xl p-4">
                      <p className="text-sm font-medium mb-2">Eligibility:</p>
                      <ul className="space-y-1">
                        {scheme.eligibility.map((item) => (
                          <li
                            key={item}
                            className="flex items-start gap-2 text-sm text-muted-foreground"
                          >
                            <FiCheck className="size-4 text-primary mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-accent/10 border border-accent/30 rounded-xl p-4">
            <p className="text-sm flex items-start gap-2">
              <FiAlertCircle className="size-4 text-primary mt-0.5 flex-shrink-0" />
              <span>
                <strong>Current for 2026:</strong> The Home Guarantee Scheme has
                unlimited places and no income caps. Help to Buy is now live
                with income thresholds of $100k (individual) / $160k (joint).
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* State Selector */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Select Your State</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              State & Territory Grants
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each state and territory offers different grants and concessions.
              Select your location to see what's available.
            </p>
          </div>

          {/* State Buttons */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {(Object.keys(stateData) as StateCode[]).map((code) => (
              <button
                key={code}
                onClick={() => setSelectedState(code)}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium text-sm transition-all",
                  selectedState === code
                    ? "bg-primary text-primary-foreground"
                    : "bg-white text-muted-foreground hover:bg-white/80 border border-border/50"
                )}
              >
                {code}
              </button>
            ))}
          </div>

          {/* State Info Card */}
          <div className="bg-white rounded-3xl border border-border/50 overflow-hidden">
            {/* Header */}
            <div className="bg-primary text-primary-foreground p-6 md:p-8">
              <div className="flex items-center gap-3 mb-2">
                <FiMapPin className="size-5" />
                <span className="text-primary-foreground/90">{state.code}</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl">{state.name}</h3>
              {state.highlight && (
                <span className="inline-block mt-3 px-3 py-1 bg-accent text-accent-foreground text-sm font-medium rounded-full">
                  {state.highlight}
                </span>
              )}
            </div>

            {/* Content */}
            <div className="p-6 md:p-8 space-y-8">
              {/* FHOG */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-accent/20 rounded-lg">
                    <FiDollarSign className="size-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold">First Home Owner Grant</h4>
                    <p className="text-2xl font-bold text-primary">
                      {state.fhog.amount}
                    </p>
                  </div>
                </div>
                <div className="bg-muted rounded-xl p-4 mb-4">
                  <p className="text-sm text-muted-foreground mb-2">
                    <strong>Property value cap:</strong>{" "}
                    {state.fhog.propertyValueCap}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>New homes only:</strong>{" "}
                    {state.fhog.newHomesOnly ? "Yes" : "No (includes existing)"}
                  </p>
                </div>
                <ul className="space-y-2">
                  {state.fhog.conditions.map((condition) => (
                    <li
                      key={condition}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <FiCheck className="size-4 text-primary mt-0.5 flex-shrink-0" />
                      {condition}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Stamp Duty */}
              <div className="pt-6 border-t border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-accent/20 rounded-lg">
                    <FiPercent className="size-5 text-primary" />
                  </div>
                  <h4 className="font-semibold">Stamp Duty Concessions</h4>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-muted rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-1">
                      Full Exemption
                    </p>
                    <p className="font-semibold">
                      {state.stampDuty.exemptionThreshold}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-1">
                      Concession up to
                    </p>
                    <p className="font-semibold">
                      {state.stampDuty.concessionThreshold}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {state.stampDuty.notes.map((note) => (
                    <li
                      key={note}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <FiCheck className="size-4 text-primary mt-0.5 flex-shrink-0" />
                      {note}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Additional Schemes */}
              {state.additionalSchemes.length > 0 && (
                <div className="pt-6 border-t border-border">
                  <h4 className="font-semibold mb-4">Additional Schemes</h4>
                  <div className="space-y-3">
                    {state.additionalSchemes.map((scheme) => (
                      <div
                        key={scheme.name}
                        className="bg-primary/5 rounded-xl p-4"
                      >
                        <p className="font-medium mb-1">{scheme.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {scheme.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-serif text-3xl md:text-4xl mb-8">
            Tips for First Home Buyers
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Start saving early",
                description:
                  "Even with low deposit schemes, having more savings gives you better rates and more options.",
              },
              {
                title: "Get pre-approved",
                description:
                  "Know your budget before you start looking. Pre-approval is free and shows sellers you're serious.",
              },
              {
                title: "Combine grants and schemes",
                description:
                  "Federal and state schemes can be used together. A broker can help you maximise your benefits.",
              },
              {
                title: "Factor in all costs",
                description:
                  "Beyond the deposit, budget for stamp duty, legal fees, inspections, and moving costs.",
              },
              {
                title: "Consider location carefully",
                description:
                  "Think about commute, amenities, growth potential, and future needs.",
              },
              {
                title: "Use a broker",
                description:
                  "Brokers are free and can access 70+ lenders to find the best deal for your situation.",
              },
            ].map((tip) => (
              <div key={tip.title} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
                  <FiCheck className="size-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">{tip.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {tip.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Ready to buy?"
        title="Let us help you navigate the grants and find the right loan"
        description="Book a free consultation and we'll walk you through your options."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{ label: "Use Calculators", href: "/calculators" }}
      />
    </>
  );
}
